// THE HOME WIDGET (?) PANEL'S PLACEMENT (WS-WIDGET-HELP-TOOLTIP-ANCHOR-1;
// Nika 2026-10-09: "fix (?) tooltip position").
//
// The brand QuestionMarkTooltip parks its panel at `left: 50%` of its
// containing block. In the widget header that block is the HEADER (2026-09-10:
// a panel anchored to the 15px glyph tracked the title's length and hung 64px
// outside the card, and its `100%` width cap measured the glyph), so the panel
// opened under the header's middle — ~800px from its (?) on an XL card
// (measured on staging vault.pr, 2560 wide: glyph x 486, panel centre x 1280).
//
// The header stays the CLAMP — the panel lives inside it and its width cap is
// the card's — and the panel is PLACED at its glyph:
//   • centred on the (?), shifted inward only as far as the header and the app
//     frame need (a 390px phone included); a panel wider than the room starts
//     at the room's start;
//   • below the (?); above it when the room below is short of the panel and
//     the room above is larger — never over the glyph.
// The brand panel draws no arrow (its `:before` is `content: none`, measured
// on staging 2026-10-09), so the panel's own edge is what meets the (?).
// The gap between the glyph and the panel is the `Y` spacing token, added in
// CSS so it scales with the type like every other gap.
//
// Measured through DOMQL elements: the panel's (?) is `el.parent`, the header
// is the nearest ancestor keyed `Header`, the app frame is the outermost
// element (the same frame the brand's own edge guard measures — the product
// shell scrolls inside it, so it is the viewport). No document lookups.

const _num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const _rect = (r) =>
  r &&
  _num(r.left) !== null &&
  _num(r.top) !== null &&
  _num(r.right) !== null &&
  _num(r.bottom) !== null
    ? r
    : null

// Pure: { glyph, box, frame } are client rects ({ left, top, right, bottom }),
// `panel` is the panel's own size { w, h }. Returns the panel's place in the
// BOX's coordinates — { left, top, side: 'below' | 'above', glyphX } (whole
// px; `glyphX` = the (?)'s centre from the panel's left edge) — or null when a
// rect is missing.
export const homeHelpTipPlace = (input) => {
  if (!input || typeof input !== 'object') return null
  const glyph = _rect(input.glyph)
  const box = _rect(input.box)
  const frame = _rect(input.frame)
  const w = input.panel && _num(input.panel.w)
  const h = input.panel && _num(input.panel.h)
  if (!glyph || !box || !frame || w === null || h === null) return null
  const cx = (glyph.left + glyph.right) / 2
  const minLeft = Math.max(box.left, frame.left)
  const maxRight = Math.min(box.right, frame.right)
  const absLeft = Math.max(minLeft, Math.min(cx - w / 2, maxRight - w))
  const left = Math.round(absLeft - box.left)
  // Bands the frame does not lend: the app header at the top, the composer
  // (prompt bar) at the bottom (homeHelpTipReserve).
  const reserve = input.reserve && typeof input.reserve === 'object' ? input.reserve : {}
  const reserveTop = _num(reserve.top) || 0
  const reserveBottom = _num(reserve.bottom) || 0
  const roomBelow = frame.bottom - reserveBottom - glyph.bottom
  const roomAbove = glyph.top - frame.top - reserveTop
  // the glyph's own height stands in for the CSS gap in the room test
  const need = h + (glyph.bottom - glyph.top)
  const side = roomBelow < need && roomAbove > roomBelow ? 'above' : 'below'
  const top = Math.round(side === 'above' ? glyph.top - box.top - h : glyph.bottom - box.top)
  return { left, top, side, glyphX: Math.round(cx - (box.left + left)) }
}

// The bands at the frame's edges that are not room for the panel, in px: the
// app header (`--nav-clear`, the navbar's live clearance) and — only while the composer shows
// (`<html data-prompt="shown">`) — its live clearance (`--prompt-clear`,
// ResizeObserver-maintained in app.js; it RETAINS its last value after the
// composer hides, hence the gate). Both are @property <length>s, so their
// computed values are px. Read off the panel's own document, the way
// homeWidgetMotion reads computed style. A value that is not plain px reads 0.
export const homeHelpTipReserve = (node) => {
  const doc = node && node.ownerDocument
  const view = doc && doc.defaultView
  if (!view || typeof view.getComputedStyle !== 'function') return { top: 0, bottom: 0 }
  const cs = view.getComputedStyle(doc.documentElement)
  const px = (name) => {
    const m = /^\s*(-?\d+(?:\.\d+)?)px\s*$/.exec(String(cs.getPropertyValue(name) || ''))
    return m ? Math.max(0, Number(m[1])) : 0
  }
  const shown =
    doc.documentElement &&
    typeof doc.documentElement.getAttribute === 'function' &&
    doc.documentElement.getAttribute('data-prompt') === 'shown'
  return { top: px('--nav-clear'), bottom: shown ? px('--prompt-clear') : 0 }
}

// THE MEASURE — at EVENT time only, never in render (staging perf profile of
// f477f8bb1: measuring inside the panel's `style` read three rects and the
// panel's size on EVERY render of EVERY card — the #2 boot cost, 1,747 ms of
// forced layout over 8 calls). `this` = the (?) button: the frame wires it to
// the button's own `pointerenter` (mouse, pen and touch fire it before the
// brand's hover/tap open; smbls captures the pointer pair at the root since
// e35151ffc, 2026-09-22 — the shell requires ^3.14.824), so a hover opens
// already placed. Any other open (keyboard focus, code) measures once from
// the panel's style at that open (homeWidgetHelpTipStyle).
//
// THE PLACE LIVES ON THE CARD (ws crm review of d82fa862c): it is written to
// the card's own state row (`_helpTipPlace: { left, top, side }`), never the
// root — a root write would re-run every root-bound subscriber on the board;
// this one re-runs only this card's panel.
export const homeWidgetHelpTipMeasure = function homeWidgetHelpTipMeasure() {
  const trigger = this
  const panel = trigger && trigger.TooltipHidden
  const s = trigger && trigger.state
  if (!panel || !s || typeof s.update !== 'function') return null
  let header = trigger.parent
  while (header && header.key !== 'Header') header = header.parent
  let frame = trigger
  while (frame && frame.parent && frame.parent.node) frame = frame.parent
  const nodes = [trigger.node, panel.node, header && header.node, frame && frame.node]
  if (nodes.some((n) => !n || typeof n.getBoundingClientRect !== 'function')) return null
  const place = this.call('homeHelpTipPlace', {
    glyph: trigger.node.getBoundingClientRect(),
    box: header.node.getBoundingClientRect(),
    frame: frame.node.getBoundingClientRect(),
    panel: { w: panel.node.offsetWidth, h: panel.node.offsetHeight },
    reserve: this.call('homeHelpTipReserve', panel.node)
  })
  if (!place) return null
  const next = { left: place.left, top: place.top, side: place.side }
  s.update({ _helpTipPlace: next })
  return next
}

// The place goes STALE when the pointer leaves the (?), focus leaves it, or
// the window resizes (homeWidgetHelpTipClose) — while the tip is not open
// for this (?): a stale place is never shown open again (the next approach
// measures afresh), but a panel fading out keeps it, so the fade never jumps. `this` = the (?) button or the panel
// (both read the card's state row).
export const homeWidgetHelpTipForget = function homeWidgetHelpTipForget() {
  const s = this && this.state
  const place = s && s._helpTipPlace
  if (!place || place.stale || typeof s.update !== 'function') return
  // still open for THIS (?) (the brand keeps it open while it has focus): the
  // place is still the one on screen — never pull it from under the panel
  const trigger = this.TooltipHidden ? this : this.parent
  const open = s.root && s.root.helpTooltipOpenId
  if (open && trigger && typeof trigger.call === 'function') {
    if (trigger.call('exec', trigger.helpTipId, trigger) === open) return
  }
  s.update({ _helpTipPlace: { ...place, stale: true } })
}

// The panel's inline style — `this` is the panel (TooltipHidden) element. It
// reads NO layout: the place is the one the measure stored on THIS card.
// Open, it shows only a fresh place (a stale one, or none, leaves the brand's
// placement); closed, it keeps any place so a fade-out stays where it was.
export const homeWidgetHelpTipStyle = function homeWidgetHelpTipStyle() {
  const el = this
  const shown = !!(el && el.isShown)
  const reveal = shown
    ? { opacity: '1', visibility: 'visible' }
    : { opacity: '0', visibility: 'hidden' }
  let place = el && el.state && el.state._helpTipPlace
  // OPENED WITHOUT AN APPROACH — keyboard focus (the brand opens on focus) or
  // code (a root write of helpTooltipOpenId): no fresh place yet, so measure
  // ONCE, now. Only at an open — a closed or freshly placed panel never reads
  // layout (ws crm note on 2e2f488c0).
  if (shown && (!place || place.stale) && el.parent && typeof el.parent.call === 'function') {
    place = el.parent.call('homeWidgetHelpTipMeasure') || place
  }
  if (!place || (shown && place.stale)) return reveal
  const above = place.side === 'above'
  return {
    left: place.left + 'px',
    right: 'auto',
    top: above
      ? 'calc(' + place.top + 'px - var(--spacing-Y))'
      : 'calc(' + place.top + 'px + var(--spacing-Y))',
    bottom: 'auto',
    transform: 'none',
    ...reveal
  }
}

// A window resize moves the (?) under an open panel whose place was measured
// for the old size — the panel closes (`this` = the panel; only its own open
// tip, never another card's).
export const homeWidgetHelpTipClose = function homeWidgetHelpTipClose() {
  const trigger = this && this.parent
  const root = this && this.state && this.state.root
  if (!trigger || !root || typeof root.update !== 'function') return
  if (!root.helpTooltipOpenId) {
    homeWidgetHelpTipForget.call(this)
    return
  }
  const id = trigger.call('exec', trigger.helpTipId, trigger)
  if (id && root.helpTooltipOpenId === id) root.update({ helpTooltipOpenId: null })
  // its place was measured for the old size
  homeWidgetHelpTipForget.call(this)
}

// Whether THIS card's (?) is open (`this` = the card frame). The open key is
// read first, unconditionally, so the attribute that asks re-runs on every
// open and close. A card whose help panel is open is lifted over its
// neighbours: a long help on a short card runs into the next card's header,
// which (z 2, later in the page) painted over it.
export const homeWidgetHelpOpen = function homeWidgetHelpOpen(s) {
  const open = s && s.root && s.root.helpTooltipOpenId
  if (!open) return false
  const q = this && this.Header && this.Header.TitleRow && this.Header.TitleRow.QuestionMarkTooltip
  if (!q || typeof q.call !== 'function') return false
  return q.call('exec', q.helpTipId, q) === open
}
